function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.language-toggle__btn):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      const submenu = summaryElement.nextElementSibling;
      const firstVisibleFocusable =
        getFocusableElements(submenu).find((element) => element.offsetParent !== null) || submenu;
      trapFocus(submenu, firstVisibleFocusable);
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    if (detailsElement === this.mainDetailsToggle) {
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
      return;
    }
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.edgeAware = this.slider?.classList.contains('slider--edge-aware');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.sliderControlWrapper = this.querySelector('.slider-buttons');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) {
      this.totalPages = 1;
      this.currentPage = 1;
      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = this.currentPage;
        this.pageTotalElement.textContent = this.totalPages;
      }
      this.updateEdgeAwareClasses();
      if (!this.enableSliderLooping && this.prevButton && this.nextButton) {
        this.prevButton.setAttribute('disabled', 'disabled');
        this.nextButton.setAttribute('disabled', 'disabled');
      }
      this.updateSliderControlsVisibility();
      return;
    }
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    // Use real slide offsets as valid "page starts" to avoid sub-pixel drift and clipped last/next cards.
    this.maxScrollLeft = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    this.pageStartSlides = this.sliderItemsToShow.filter((element) => element.offsetLeft <= this.maxScrollLeft + 1);
    this.totalPages = Math.max(1, this.pageStartSlides.length);
    this.updateSliderControlsVisibility();
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    const pageSlides = this.pageStartSlides && this.pageStartSlides.length ? this.pageStartSlides : this.sliderItemsToShow;
    let closestIndex = 0;
    let closestDiff = Number.POSITIVE_INFINITY;
    pageSlides.forEach((element, index) => {
      const diff = Math.abs(element.offsetLeft - this.slider.scrollLeft);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = index;
      }
    });
    this.currentPage = closestIndex + 1;
    this.updateEdgeAwareClasses();

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  updateSliderControlsVisibility() {
    if (!this.sliderControlWrapper) return;
    const hideControls = Number(this.totalPages) <= 1;
    this.sliderControlWrapper.toggleAttribute('hidden', hideControls);
    if (hideControls) {
      this.sliderControlWrapper.style.display = 'none';
    } else {
      this.sliderControlWrapper.style.removeProperty('display');
    }
  }

  updateEdgeAwareClasses() {
    if (!this.edgeAware || !this.sliderItemsToShow || !this.sliderItemsToShow.length) return;

    this.sliderItemsToShow.forEach((slide) => {
      slide.classList.remove('slider__slide--first-visible', 'slider__slide--last-visible', 'slider__slide--visible');
    });

    const firstSlideWidth = this.sliderItemsToShow[0]?.clientWidth || 0;
    const visibleCount = firstSlideWidth > 0 ? Math.max(1, Math.round(this.slider.clientWidth / firstSlideWidth)) : 1;

    // Prefer deterministic edges from current page + visible count.
    // This avoids sub-pixel flicker where desktop 3-up occasionally mislabels the last visible card.
    const pageSlides = this.pageStartSlides && this.pageStartSlides.length ? this.pageStartSlides : null;
    if (pageSlides && this.currentPage) {
      const startPageIndex = Math.min(pageSlides.length - 1, Math.max(0, this.currentPage - 1));
      const firstSlide = pageSlides[startPageIndex];
      const firstIndex = this.sliderItemsToShow.indexOf(firstSlide);
      if (firstIndex > -1) {
        const lastIndex = Math.min(this.sliderItemsToShow.length - 1, firstIndex + visibleCount - 1);
        for (let i = firstIndex; i <= lastIndex; i += 1) {
          this.sliderItemsToShow[i].classList.add('slider__slide--visible');
        }
        this.sliderItemsToShow[firstIndex].classList.add('slider__slide--first-visible');
        this.sliderItemsToShow[lastIndex].classList.add('slider__slide--last-visible');
        return;
      }
    }

    const viewportLeft = this.slider.scrollLeft + 0.5;
    const viewportRight = this.slider.scrollLeft + this.slider.clientWidth - 0.5;
    let firstVisible = null;
    let lastVisible = null;

    this.sliderItemsToShow.forEach((slide) => {
      const slideLeft = slide.offsetLeft;
      const slideRight = slideLeft + slide.clientWidth;
      if (slideRight <= viewportLeft || slideLeft >= viewportRight) return;
      slide.classList.add('slider__slide--visible');
      if (!firstVisible) firstVisible = slide;
      lastVisible = slide;
    });

    if (!firstVisible) firstVisible = this.sliderItemsToShow[0];
    if (!lastVisible) lastVisible = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];

    firstVisible.classList.add('slider__slide--first-visible');
    lastVisible.classList.add('slider__slide--last-visible');
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const pageSlides = this.pageStartSlides && this.pageStartSlides.length ? this.pageStartSlides : this.sliderItemsToShow;
    if (!pageSlides.length) return;

    const step = Number(event.currentTarget.dataset.step) || 1;
    const direction = event.currentTarget.name === 'next' ? 1 : -1;
    const currentIndex = Math.max(0, (this.currentPage || 1) - 1);
    const targetIndex = Math.min(pageSlides.length - 1, Math.max(0, currentIndex + direction * step));
    this.slideScrollPosition = pageSlides[targetIndex].offsetLeft;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    const maxScrollLeft = Number.isFinite(this.maxScrollLeft)
      ? this.maxScrollLeft
      : Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    const targetPosition = Math.max(0, Math.min(maxScrollLeft, position));
    this.slider.scrollTo({
      left: targetPosition,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    this.announcementBarArrowButtonWasClicked = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;
    this.restartAutoplay();

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  restartAutoplay() {
    if (this.slider.getAttribute('data-autoplay') !== 'true' || this.sliderAutoplayButton) return;

    clearTimeout(this.autoplayRestartTimeout);
    this.pause();

    if (this.reducedMotion.matches) return;

    this.autoplayRestartTimeout = setTimeout(() => {
      this.play();
    }, this.autoplaySpeed);
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if ((this.announcementBarSlider && this.announcementBarSlider.contains(event.target)) || this.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
    this.restartAutoplay();
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

class MobileFooterTopButton {
  constructor() {
    this.button = document.querySelector('[data-mobile-footer-top-button]');
    this.footerGroup = document.querySelector('.site-footer-group');
    this.mobileMedia = window.matchMedia('(max-width: 989px)');
    this.footerVisible = false;
    this.observer = null;

    if (!this.button || !this.footerGroup) return;

    this.onScroll = this.updateVisibility.bind(this);
    this.onMediaChange = this.setup.bind(this);

    this.button.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    });

    window.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.mobileMedia.addEventListener) {
      this.mobileMedia.addEventListener('change', this.onMediaChange);
    } else if (this.mobileMedia.addListener) {
      this.mobileMedia.addListener(this.onMediaChange);
    }

    this.setup();
  }

  setup() {
    this.footerVisible = false;
    this.button.classList.remove('is-visible');
    this.observer?.disconnect();

    if (!this.mobileMedia.matches) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        this.footerVisible = entries[0]?.isIntersecting || false;
        this.updateVisibility();
      },
      {
        threshold: 0.05,
      }
    );

    this.observer.observe(this.footerGroup);
    this.updateVisibility();
  }

  updateVisibility() {
    if (!this.button || !this.mobileMedia.matches) return;

    const hasScrolledEnough = window.scrollY > Math.max(120, window.innerHeight * 0.35);
    this.button.classList.toggle('is-visible', this.footerVisible && hasScrolledEnough);
  }
}

new MobileFooterTopButton();

class XCloudSearchFormRouter {
  constructor() {
    if (!this.hasXCloudSearch()) return;

    this.routeForms();
    document.addEventListener('submit', (event) => this.routeSubmittedForm(event), true);
    document.addEventListener('shopify:section:load', (event) => this.routeForms(event.target));
  }

  hasXCloudSearch() {
    const settingsElement = document.getElementById('cloudsearch-settings');
    if (!settingsElement) return false;

    try {
      const settings = JSON.parse(settingsElement.textContent);
      return typeof settings.srp === 'string' && settings.srp.length > 0;
    } catch (error) {
      return false;
    }
  }

  routeForms(root = document) {
    root.querySelectorAll('form[data-xcloud-search-url]').forEach((form) => this.routeForm(form));
  }

  routeSubmittedForm(event) {
    const form = event.target.closest?.('form[data-xcloud-search-url]');
    if (form) this.routeForm(form);
  }

  routeForm(form) {
    const xcloudSearchUrl = form.dataset.xcloudSearchUrl;
    if (xcloudSearchUrl) form.setAttribute('action', xcloudSearchUrl);
  }
}

new XCloudSearchFormRouter();

class XCloudSidebarFilters {
  constructor() {
    this.roots = Array.from(document.querySelectorAll('#cloud_search_filters_root'));
    if (!this.roots.length) return;

    this.layoutMedia = window.matchMedia('(min-width: 990px)');
    this.onLayoutChange = this.handleLayoutChange.bind(this);
    this.observers = [];
    this.rootStates = new WeakMap();
    this.roots.forEach((root) => this.setupRoot(root));

    if (this.layoutMedia.addEventListener) {
      this.layoutMedia.addEventListener('change', this.onLayoutChange);
    } else if (this.layoutMedia.addListener) {
      this.layoutMedia.addListener(this.onLayoutChange);
    }
  }

  getScope(root) {
    return root.closest('.shopify-section') || root.closest('.section') || root.closest('.catalog-layout') || document.body;
  }

  isDesktopAppliedFiltersLayout() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  handleLayoutChange() {
    this.roots.forEach((root) => this.scheduleSync(root));
  }

  getRootState(root) {
    let state = this.rootStates.get(root);

    if (!state) {
      state = {
        scheduled: false,
        syncing: false,
      };
      this.rootStates.set(root, state);
    }

    return state;
  }

  setupRoot(root) {
    this.scheduleSync(root);

    const observerTarget = this.getScope(root);

    const observer = new MutationObserver(() => {
      this.scheduleSync(root);
    });

    observer.observe(observerTarget, { childList: true, subtree: true });
    this.observers.push(observer);
  }

  scheduleSync(root) {
    const state = this.getRootState(root);
    if (state.scheduled) return;

    state.scheduled = true;

    window.requestAnimationFrame(() => {
      state.scheduled = false;
      this.syncRoot(root);
    });
  }

  syncRoot(root) {
    if (!root?.isConnected) return;

    const state = this.getRootState(root);
    if (state.syncing) return;

    const scope = this.getScope(root);
    const sidebarBox = root.closest('.catalog-sidebar__box');
    if (!sidebarBox) return;

    state.syncing = true;

    try {
      const host = this.ensureAppliedFiltersHost(scope, sidebarBox);
      this.moveAppliedFiltersToHost(scope, host);
      this.removeNativeClearAll(host);
      this.normalizeAppliedFilters(host);

      const inlineAppliedFilters = this.hostHasNativeAppliedFilters(host) ? [] : this.collectInlineAppliedFilters(root);
      this.renderInlineAppliedFilters(host, inlineAppliedFilters);
      this.updateClearAllLink(sidebarBox, host);
      this.updateMobileTriggerCount(scope, host);

      this.restoreFilterLabels(root);
    } finally {
      state.syncing = false;
    }
  }

  ensureAppliedFiltersHost(scope, sidebarBox) {
    if (this.isDesktopAppliedFiltersLayout()) {
      const desktopHost = scope.querySelector('[data-desktop-applied-filters-host]');
      if (desktopHost) return desktopHost;
    }

    let host = sidebarBox.querySelector('.xcloud-applied-filters-host');
    if (!host) {
      host = document.createElement('div');
      host.className = 'xcloud-applied-filters-host';
      sidebarBox.insertBefore(host, sidebarBox.firstElementChild);
    } else if (sidebarBox.firstElementChild !== host) {
      sidebarBox.insertBefore(host, sidebarBox.firstElementChild);
    }

    return host;
  }

  removeNativeClearAll(host) {
    Array.from(host.children).forEach((child) => {
      if (!(child instanceof Element)) return;
      if (!this.isNativeClearAllElement(child)) return;

      child.remove();
    });
  }

  isNativeClearAllElement(element) {
    if (element.matches('.xcloud-applied-filter-pill, .cloud-search-applied-filter--squared')) return false;

    const className = typeof element.className === 'string' ? element.className.toLowerCase() : '';
    const text = element.textContent.replace(/\s+/g, ' ').trim().toLowerCase();

    if (className.includes('clear-all')) return true;

    return [
      'cancella tutti i filtri',
      'cancella filtri',
      'clear all filters',
      'clear filters',
    ].includes(text);
  }

  updateClearAllLink(sidebarBox, host) {
    const clearLink = sidebarBox.querySelector('[data-mobile-clear-filters]');
    if (!clearLink) return;

    const hasActiveFilters = Array.from(host.children).some((child) => child instanceof Element);
    clearLink.hidden = !hasActiveFilters;
    clearLink.href = hasActiveFilters ? this.buildClearAllUrl() : window.location.pathname;
  }

  updateMobileTriggerCount(scope, host) {
    const count = host.querySelectorAll('.cloud-search-applied-filter--squared, .xcloud-applied-filter-pill').length;

    scope.querySelectorAll('[data-mobile-filters-controls] .cloud-search-filters-link-text').forEach((label) => {
      const baseText = label.dataset.baseText || label.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      label.dataset.baseText = baseText;
      label.textContent = `${baseText} (${count})`;
    });
  }

  buildClearAllUrl() {
    const url = new URL(window.location.href);

    Array.from(url.searchParams.keys()).forEach((key) => {
      if (/^filter([_.\[]|$)/i.test(key) || /^page$/i.test(key) || /^constraint$/i.test(key)) {
        url.searchParams.delete(key);
      }
    });

    const search = url.searchParams.toString();
    return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
  }

  moveAppliedFiltersToHost(scope, host) {
    const appliedContainers = Array.from(
      scope.querySelectorAll('.cloud-search-filters-sidebar__applied-filters')
    ).filter((container) => container !== host && !host.contains(container));

    appliedContainers.forEach((container) => {
      while (container.firstChild) {
        host.appendChild(container.firstChild);
      }

      container.remove();
    });

    Array.from(scope.querySelectorAll('.cloud-search-applied-filter--squared'))
      .filter((chip) => !host.contains(chip))
      .forEach((chip) => {
        host.appendChild(chip);
      });
  }

  hostHasNativeAppliedFilters(host) {
    return Array.from(host.children).some((child) => {
      if (!(child instanceof Element)) return false;
      if (child.classList.contains('xcloud-applied-filter-pill')) return false;

      return child.matches('.cloud-search-applied-filter--squared, .cloud-search-filter__name, .cloud-search-filters-sidebar__applied-filters');
    });
  }

  normalizeAppliedFilters(host) {
    host.querySelectorAll('.cloud-search-applied-filter--squared').forEach((filterChip) => {
      const label = filterChip.querySelector('.cloud-search-filter__name');
      const appliedText = label?.querySelector('.cloud-search-applied-filter__text');
      const values = appliedText?.querySelector('.cloud-search-applied-filter__values');
      const clearButton = filterChip.querySelector('.cloud-search-applied-filter__clear');

      if (label) {
        const baseLabel = label.dataset.xcloudBaseLabel
          || label.childNodes[0]?.textContent?.trim()
          || label.textContent.split(':')[0].trim();
        const selectedValues = values?.textContent?.replace(/\s+/g, ' ').trim();

        label.dataset.xcloudBaseLabel = baseLabel;

        if (selectedValues) {
          label.textContent = `${baseLabel}: ${selectedValues}`;
        } else {
          label.textContent = baseLabel;
        }
      }

      if (clearButton) {
        clearButton.textContent = '×';
        clearButton.setAttribute('aria-label', 'Rimuovi filtro');
      }
    });
  }

  collectInlineAppliedFilters(root) {
    const filters = [];
    const seen = new Set();

    root.querySelectorAll('.cloud-search-filter__name').forEach((label) => {
      if (label.closest('.xcloud-applied-filters-host')) return;

      const appliedText = label.querySelector('.cloud-search-applied-filter__text');
      if (!appliedText) return;

      const valuesElement = appliedText.querySelector('.cloud-search-applied-filter__values');
      const baseLabel = label.dataset.xcloudBaseLabel
        || label.childNodes[0]?.textContent?.replace(/\s+/g, ' ').trim()
        || label.textContent.split(':')[0].trim();
      const selectedValues = (
        valuesElement?.textContent
        || appliedText.textContent.replace(/^:\s*/, '')
      ).replace(/\s+/g, ' ').trim();

      if (!baseLabel || !selectedValues) return;

      const key = `${baseLabel}::${selectedValues}`;
      if (seen.has(key)) return;

      seen.add(key);
      filters.push({
        baseLabel,
        selectedValues,
        actionElement: this.findInlineAppliedFilterAction(root, baseLabel, selectedValues),
      });
    });

    return filters;
  }

  normalizeAppliedFilterValue(value) {
    return value?.replace(/\s+/g, ' ').trim().toLowerCase() || '';
  }

  findInlineAppliedFilterAction(root, baseLabel, selectedValues) {
    const normalizedLabel = this.normalizeAppliedFilterValue(baseLabel);
    const normalizedValues = this.normalizeAppliedFilterValue(selectedValues);

    const candidates = root.querySelectorAll(
      '.cloud-search-page-controls__applied-filters [data-filter-name], .cloud-search-filters-sidebar__applied-filters [data-filter-name], .cloud-search-applied-filter--rounded, .cloud-search-applied-filter--squared'
    );

    for (const candidate of candidates) {
      if (!(candidate instanceof Element)) continue;

      const candidateLabel = this.normalizeAppliedFilterValue(
        candidate.dataset.filterName
          || candidate.querySelector('.cloud-search-applied-filter__name, .cloud-search-filter__name')?.childNodes[0]?.textContent
          || candidate.textContent.split(':')[0]
      );
      const candidateValues = this.normalizeAppliedFilterValue(
        candidate.querySelector('.cloud-search-applied-filter__values')?.textContent
      );

      if (candidateLabel !== normalizedLabel || candidateValues !== normalizedValues) continue;

      const clearButton = candidate.querySelector('.cloud-search-applied-filter__clear');
      if (clearButton) return clearButton;
    }

    return null;
  }

  renderInlineAppliedFilters(host, filters) {
    const signature = filters
      .map((filter) => `${filter.baseLabel}::${filter.selectedValues}::${filter.actionElement ? '1' : '0'}`)
      .join('||');

    if (host.dataset.xcloudInlineSignature === signature) return;

    host.dataset.xcloudInlineSignature = signature;
    host.querySelectorAll('.xcloud-applied-filter-pill').forEach((pill) => pill.remove());

    if (!filters.length) return;

    const fragment = document.createDocumentFragment();

    filters.forEach((filter) => {
      const pill = document.createElement(filter.actionElement ? 'button' : 'div');
      pill.className = 'xcloud-applied-filter-pill';

      if (pill instanceof HTMLButtonElement) {
        pill.type = 'button';
        pill.setAttribute('aria-label', `Rimuovi filtro ${filter.baseLabel}: ${filter.selectedValues}`);
        pill.addEventListener('click', () => {
          filter.actionElement?.click();
        });
      }

      const label = document.createElement('span');
      label.className = 'xcloud-applied-filter-pill__label';
      label.textContent = `${filter.baseLabel}: ${filter.selectedValues}`;
      pill.appendChild(label);

      if (filter.actionElement) {
        const clear = document.createElement('span');
        clear.className = 'xcloud-applied-filter-pill__clear';
        clear.setAttribute('aria-hidden', 'true');
        clear.textContent = '×';
        pill.appendChild(clear);
      }

      fragment.appendChild(pill);
    });

    host.prepend(fragment);
  }

  restoreFilterLabels(root) {
    const candidates = root.querySelectorAll('button, summary, [role="button"], label, .cloud-search-filter__name');

    candidates.forEach((element) => {
      if (element.closest('.template-search__enhanced-options')) return;
      if (element.closest('.xcloud-applied-filters-host')) return;

      const text = element.textContent.replace(/\s+/g, ' ').trim();
      if (!text || !text.includes(':')) return;

      const baseLabel = element.dataset.xcloudBaseLabel || text.split(':')[0].trim();
      if (!baseLabel) return;

      element.dataset.xcloudBaseLabel = baseLabel;

      if (!element.children.length) {
        element.textContent = baseLabel;
        return;
      }

      const firstTextNode = Array.from(element.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
      if (firstTextNode) {
        firstTextNode.textContent = baseLabel;
      }
    });
  }
}

new XCloudSidebarFilters();

class XCloudFilterDropdownPositioner {
  constructor() {
    this.root = document.getElementById('cloud_search_filters_root');
    if (!this.root) return;

    this.gap = 10;
    this.viewportPadding = 8;
    this.valuesMaxHeight = 235;
    this.menuChromeHeight = 30;
    this.scheduled = false;

    this.schedule = this.schedule.bind(this);
    this.scheduleWithFollowUp = this.scheduleWithFollowUp.bind(this);

    this.observer = new MutationObserver(this.scheduleWithFollowUp);
    this.observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('click', this.scheduleWithFollowUp, true);
    document.addEventListener('keydown', this.scheduleWithFollowUp, true);
    window.addEventListener('resize', this.scheduleWithFollowUp, { passive: true });
    window.addEventListener('scroll', this.scheduleWithFollowUp, { passive: true });

    this.scheduleWithFollowUp();
  }

  schedule() {
    if (this.scheduled) return;

    this.scheduled = true;
    window.requestAnimationFrame(() => {
      this.scheduled = false;
      this.sync();
    });
  }

  scheduleWithFollowUp() {
    this.schedule();
    window.setTimeout(this.schedule, 80);
    window.setTimeout(this.schedule, 180);
  }

  sync() {
    const trigger = this.findOpenTrigger();

    if (!trigger) {
      this.restoreManagedMenus();
      return;
    }

    const menu = this.findMenuForTrigger(trigger);
    if (!menu) return;

    if (menu.dataset.xcloudDropupActive === 'true') {
      this.restoreMenu(menu);
    }

    const triggerRect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();

    if (!menuRect.width || !menuRect.height) return;

    const menuHeight = menuRect.height;
    const nativeBottom = triggerRect.bottom + this.gap + menuHeight;
    const viewportBottom = window.innerHeight - this.viewportPadding;

    if (nativeBottom <= viewportBottom) return;

    this.positionAbove(menu, triggerRect, menuRect);
  }

  findOpenTrigger() {
    return Array.from(
      this.root.querySelectorAll('.cloud-search-filter--open[data-filter-name]')
    ).find((element) => !element.classList.contains('cloud-search-filter-values-container')) || null;
  }

  findMenuForTrigger(trigger) {
    const filterName = trigger.dataset.filterName;
    const menus = Array.from(
      document.querySelectorAll('.cloud-search-filter-values-container.cloud-search-filter')
    ).filter((menu) => menu instanceof HTMLElement && menu.offsetParent !== null);

    if (!filterName) return menus[0] || null;

    return menus.find((menu) => menu.dataset.filterName === filterName) || menus[0] || null;
  }

  positionAbove(menu, triggerRect, menuRect) {
    const values = menu.querySelector('.cloud-search-filter__values');
    const spaceAbove = triggerRect.top - this.gap - this.viewportPadding;
    const valuesHeight = Math.min(
      this.valuesMaxHeight,
      Math.max(80, Math.floor(spaceAbove - this.menuChromeHeight))
    );
    const positionedHeight = Math.min(menuRect.height, valuesHeight + this.menuChromeHeight);
    const menuWidth = Math.min(
      menuRect.width || menu.offsetWidth || 280,
      window.innerWidth - this.viewportPadding * 2
    );
    const left = Math.min(
      Math.max(this.viewportPadding, triggerRect.left),
      Math.max(this.viewportPadding, window.innerWidth - menuWidth - this.viewportPadding)
    );
    const top = Math.max(this.viewportPadding, triggerRect.top - positionedHeight - this.gap);

    this.storeNativeStyle(menu);

    menu.dataset.xcloudDropupActive = 'true';
    menu.style.position = 'fixed';
    menu.style.inset = 'auto';
    menu.style.left = `${Math.floor(left)}px`;
    menu.style.top = `${Math.floor(top)}px`;
    menu.style.maxWidth = `calc(100vw - ${this.viewportPadding * 2}px)`;
    menu.style.transform = 'none';
    menu.style.zIndex = '2147483646';

    if (values instanceof HTMLElement) {
      this.storeNativeStyle(values);
      values.style.maxHeight = `${valuesHeight}px`;
      values.style.overflowY = 'auto';
      values.style.overscrollBehavior = 'contain';
    }
  }

  storeNativeStyle(element) {
    if (element.dataset.xcloudNativeStyle !== undefined) return;
    element.dataset.xcloudNativeStyle = element.getAttribute('style') || '';
  }

  restoreManagedMenus() {
    document
      .querySelectorAll('.cloud-search-filter-values-container[data-xcloud-dropup-active="true"]')
      .forEach((menu) => this.restoreMenu(menu));
  }

  restoreMenu(menu) {
    if (!(menu instanceof HTMLElement)) return;

    const values = menu.querySelector('.cloud-search-filter__values');
    if (values instanceof HTMLElement) this.restoreStyle(values);

    this.restoreStyle(menu);
    delete menu.dataset.xcloudDropupActive;
  }

  restoreStyle(element) {
    if (element.dataset.xcloudNativeStyle === undefined) return;

    if (element.dataset.xcloudNativeStyle) {
      element.setAttribute('style', element.dataset.xcloudNativeStyle);
    } else {
      element.removeAttribute('style');
    }

    delete element.dataset.xcloudNativeStyle;
  }
}

new XCloudFilterDropdownPositioner();

class XCloudMobileFiltersToggle {
  constructor() {
    this.mobileMedia = window.matchMedia('(max-width: 989px)');
    this.instances = [];
    this.onMediaChange = this.syncAll.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);

    document.querySelectorAll('[data-mobile-filters-controls]').forEach((controls) => {
      const scope = controls.closest('.shopify-section') || controls.closest('.section') || document;
      const button = controls.querySelector('[data-mobile-filters-toggle]');
      const backdrop = scope.querySelector('[data-mobile-filters-backdrop]');
      const closeButton = scope.querySelector('[data-mobile-filters-close]');
      const panel = scope.querySelector('[data-mobile-filters-panel]');

      if (!button || !panel || !backdrop) return;

      if (this.mobileMedia.matches) {
        panel.hidden = true;
        backdrop.hidden = true;
        panel.setAttribute('aria-hidden', 'true');
        button.setAttribute('aria-expanded', 'false');
      }

      panel.classList.add('xcloud-mobile-filters-ready');

      const instance = {
        backdrop,
        button,
        closeTimer: null,
        closeButton,
        controls,
        openTimer: null,
        panel,
      };

      button.addEventListener('click', () => this.toggle(instance));
      backdrop.addEventListener('click', () => this.handleBackdropClick(instance));
      closeButton?.addEventListener('click', () => this.close(instance));
      this.instances.push(instance);
    });

    if (!this.instances.length) return;

    document.addEventListener('keydown', this.onKeyDown);

    if (this.mobileMedia.addEventListener) {
      this.mobileMedia.addEventListener('change', this.onMediaChange);
    } else if (this.mobileMedia.addListener) {
      this.mobileMedia.addListener(this.onMediaChange);
    }

    this.syncAll();

    window.requestAnimationFrame(() => {
      this.instances.forEach((instance) => {
        instance.panel.classList.add('xcloud-mobile-filters-mounted');
      });
    });
  }

  clearTimers(instance) {
    window.clearTimeout(instance.openTimer);
    window.clearTimeout(instance.closeTimer);
  }

  closeAll(except = null) {
    this.instances.forEach((instance) => {
      if (instance !== except && this.isOpen(instance)) {
        this.close(instance);
      }
    });
  }

  handleBackdropClick(instance) {
    if (!this.mobileMedia.matches) return;
    this.close(instance);
  }

  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    const openInstance = this.instances.find((instance) => this.isOpen(instance));
    if (!openInstance) return;

    this.close(openInstance);
  }

  isOpen(instance) {
    return instance.button.getAttribute('aria-expanded') === 'true';
  }

  open(instance) {
    this.clearTimers(instance);
    this.closeAll(instance);
    instance.panel.hidden = false;
    instance.backdrop.hidden = false;
    instance.panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mobile-filters-open');
    instance.controls.classList.add('is-open');

    instance.openTimer = window.setTimeout(() => {
      instance.panel.classList.add('is-open');
      instance.backdrop.classList.add('is-visible');
      instance.button.setAttribute('aria-expanded', 'true');
    }, 120);
  }

  close(instance) {
    this.clearTimers(instance);
    instance.panel.classList.remove('is-open');
    instance.backdrop.classList.remove('is-visible');
    instance.panel.setAttribute('aria-hidden', 'true');
    instance.button.setAttribute('aria-expanded', 'false');
    instance.controls.classList.remove('is-open');

    instance.closeTimer = window.setTimeout(() => {
      instance.panel.hidden = true;
      instance.backdrop.hidden = true;
      if (!this.instances.some((item) => item !== instance && this.isOpen(item))) {
        document.body.classList.remove('mobile-filters-open');
      }
    }, 220);
  }

  syncAll() {
    this.instances.forEach((instance) => this.syncInstance(instance));
  }

  syncInstance(instance) {
    this.clearTimers(instance);

    if (this.mobileMedia.matches) {
      instance.controls.hidden = false;

      if (this.isOpen(instance)) {
        instance.panel.hidden = false;
        instance.backdrop.hidden = false;
        instance.panel.classList.add('is-open');
        instance.backdrop.classList.add('is-visible');
        instance.panel.setAttribute('aria-hidden', 'false');
        instance.controls.classList.add('is-open');
        document.body.classList.add('mobile-filters-open');
      } else {
        instance.panel.classList.remove('is-open');
        instance.backdrop.classList.remove('is-visible');
        instance.panel.setAttribute('aria-hidden', 'true');
        instance.panel.hidden = true;
        instance.backdrop.hidden = true;
        instance.controls.classList.remove('is-open');
      }

      return;
    }

    instance.controls.hidden = true;
    instance.panel.hidden = false;
    instance.panel.classList.remove('is-open');
    instance.backdrop.classList.remove('is-visible');
    instance.backdrop.hidden = true;
    instance.panel.removeAttribute('aria-hidden');
    instance.button.setAttribute('aria-expanded', 'false');
    instance.controls.classList.remove('is-open');
    document.body.classList.remove('mobile-filters-open');
  }

  toggle(instance) {
    if (!this.mobileMedia.matches) return;

    if (this.isOpen(instance)) {
      this.close(instance);
      return;
    }

    this.open(instance);
  }
}

new XCloudMobileFiltersToggle();

class XCloudSearchResultPriceVisibility {
  constructor() {
    this.cache = new Map();
    this.pending = new Map();
    this.scheduled = false;
    this.observer = null;
    this.itemSelector = 'dd[data-id], li[data-id], a.cs-item[data-id], a[data-id][role="option"]';
    this.priceSelector = '.price, [id^="cloud-search-option-"][id$="-price"]';
    this.productLinkSelector = [
      'a.cs-product-link[href*="/products/"]',
      '.details-top-image a[href*="/products/"]',
      'a.cs-item[href*="/products/"]',
      'a[data-id][role="option"][href*="/products/"]',
    ].join(', ');

    this.scan = this.scan.bind(this);
    this.scheduleScan = this.scheduleScan.bind(this);

    this.scheduleScan();
    this.setupObserver();
  }

  setupObserver() {
    if (!document.body) return;

    this.observer = new MutationObserver(() => {
      this.scheduleScan();
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  scheduleScan() {
    if (this.scheduled) return;

    this.scheduled = true;
    window.requestAnimationFrame(() => {
      this.scheduled = false;
      this.scan();
    });
  }

  scan() {
    document.querySelectorAll(this.itemSelector).forEach((item) => {
      if (!this.getProductPath(item)) return;
      if (!item.querySelector(this.priceSelector)) return;

      this.updateItemPriceVisibility(item);
    });
  }

  updateItemPriceVisibility(item) {
    const priceElements = item.querySelectorAll(this.priceSelector);
    if (!priceElements.length) return;

    const productPath = this.getProductPath(item);
    if (!productPath) return;

    if (this.cache.has(productPath)) {
      this.applyPriceVisibility(item, this.cache.get(productPath), priceElements);
      return;
    }

    this.fetchInStockStatus(productPath)
      .then((isInStock) => {
        this.cache.set(productPath, isInStock);
        this.applyPriceVisibility(item, isInStock, priceElements);
      })
      .catch(() => {
        this.cache.set(productPath, true);
        this.applyPriceVisibility(item, true, priceElements);
      });
  }

  getProductPath(item) {
    const link = item.matches(this.productLinkSelector) ? item : item.querySelector(this.productLinkSelector);
    const href = link?.getAttribute('href');
    if (!href) return null;

    try {
      const url = new URL(href, window.location.origin);
      if (!/\/products\//.test(url.pathname)) return null;
      return url.pathname.replace(/\/$/, '');
    } catch (error) {
      return null;
    }
  }

  fetchInStockStatus(productPath) {
    if (this.pending.has(productPath)) {
      return this.pending.get(productPath);
    }

    const request = fetch(`${productPath}.js`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to fetch ${productPath}.js`);
        return response.json();
      })
      .then((product) => this.resolveInStock(product))
      .finally(() => {
        this.pending.delete(productPath);
      });

    this.pending.set(productPath, request);
    return request;
  }

  applyPriceVisibility(item, isAvailable, priceElements = []) {
    item.classList.toggle('cs-product-unavailable', !isAvailable);
    priceElements.forEach((priceElement) => {
      priceElement.style.display = isAvailable ? '' : 'none';
    });
  }

  resolveInStock(product) {
    if (!product?.variants?.length) {
      return Boolean(product?.available);
    }

    return product.variants.some((variant) => {
      if (!variant) return false;
      if (!variant.available) return false;

      // inventory_quantity is often not exposed in public product JSON.
      // Use it only when present; otherwise fallback to "available".
      const hasExplicitQty = variant.inventory_quantity !== undefined && variant.inventory_quantity !== null;
      if (hasExplicitQty) {
        return Number(variant.inventory_quantity) > 0;
      }

      return true;
    });
  }
}

new XCloudSearchResultPriceVisibility();
